import { useState } from "react";
import {
  Eye,
  Download,
  Check,
  X,
  Clock,
  Filter,
  Search,
  Calendar,
  User,
  FileText,
  MoreHorizontal,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { attendanceAPI } from "@/lib/api";
import { toast } from "sonner";

export default function AbsenceRequestsTable({
  requests,
  loading,
  onRefresh,
  userRole,
  currentUserId,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [processingId, setProcessingId] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const canManageRequests = ["admin", "rh", "manager"].includes(userRole);

  const getStatusBadge = (status) => {
    const statusConfig = {
      declaree: {
        label: "En attente",
        variant: "outline",
        className: "text-orange-600",
      },
      validee: {
        label: "Approuvé",
        variant: "outline",
        className: "text-green-600",
      },
      contestee: {
        label: "Refusé",
        variant: "outline",
        className: "text-red-600",
      },
    };

    const config = statusConfig[status] || statusConfig.declaree;
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const calculateDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const handleApproval = async (requestId, approved) => {
    setProcessingId(requestId);
    try {
      const data = {
        statut: approved ? "validee" : "contestee",
        commentaire: approved ? "Demande approuvée" : "Demande refusée",
      };

      const response = await attendanceAPI.approveAbsenceRequest(
        requestId,
        data
      );

      if (response.data.success) {
        toast.success(response.data.message);
        onRefresh();
      }
    } catch (error) {
      console.error("Error updating absence request:", error);
      toast.error("Erreur lors de la mise à jour de la demande");
    } finally {
      setProcessingId(null);
    }
  };

  const handleDownloadJustification = async (requestId, fileName) => {
    try {
      const response = await attendanceAPI.downloadAbsenceJustification(
        requestId
      );

      // Create blob from response
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || `justification_${requestId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Justificatif téléchargé avec succès");
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Erreur lors du téléchargement du fichier");
    }
  };

  const handleDeleteRequest = async (requestId) => {
    setProcessingId(requestId);
    try {
      const response = await attendanceAPI.deleteAbsenceRequest(requestId);

      if (response.data.success) {
        toast.success(response.data.message);
        onRefresh();
      }
    } catch (error) {
      console.error("Error deleting absence request:", error);
      toast.error("Erreur lors de la suppression de la demande");
    } finally {
      setProcessingId(null);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setDetailsOpen(true);
  };

  const filteredRequests = requests.filter((request) => {
    const matchesSearch =
      request.employe?.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.employe?.prenom
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      request.type_absence?.nom
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      request.motif?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || request.statut === statusFilter;

    // For employees, only show their own requests
    if (userRole === "employe") {
      return (
        matchesSearch && matchesStatus && request.employe_id === currentUserId
      );
    }

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Demandes d'absence</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-16 bg-gray-100 rounded"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Demandes d'absence
            {filteredRequests.length > 0 && (
              <Badge variant="outline" className="ml-2">
                {filteredRequests.length}
              </Badge>
            )}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
          >
            Actualiser
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Rechercher par nom, type ou motif..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="declaree">En attente</SelectItem>
              <SelectItem value="validee">Approuvé</SelectItem>
              <SelectItem value="contestee">Refusé</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {filteredRequests.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">
              Aucune demande d'absence trouvée
            </p>
            <p className="text-sm text-gray-400">
              {searchTerm || statusFilter !== "all"
                ? "Essayez de modifier vos filtres"
                : "Les nouvelles demandes apparaîtront ici"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {canManageRequests && (
                    <TableHead>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Employé
                      </div>
                    </TableHead>
                  )}
                  <TableHead>Type</TableHead>
                  <TableHead>Période</TableHead>
                  <TableHead>Durée</TableHead>
                  <TableHead>Motif</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Date demande</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map((request) => (
                  <TableRow key={request.id}>
                    {canManageRequests && (
                      <TableCell>
                        <div>
                          <p className="font-medium">
                            {request.employe?.prenom} {request.employe?.nom}
                          </p>
                          <p className="text-sm text-gray-500">
                            {request.employe?.departement?.nom}
                          </p>
                        </div>
                      </TableCell>
                    )}
                    <TableCell>
                      <Badge variant="outline">
                        {request.type_absence?.nom}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{formatDate(request.date_debut)}</p>
                        <p className="text-gray-500">
                          au {formatDate(request.date_fin)}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {calculateDuration(
                          request.date_debut,
                          request.date_fin
                        )}{" "}
                        jour
                        {calculateDuration(
                          request.date_debut,
                          request.date_fin
                        ) > 1
                          ? "s"
                          : ""}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <p
                        className="text-sm max-w-[200px] truncate"
                        title={request.motif}
                      >
                        {request.motif}
                      </p>
                    </TableCell>
                    <TableCell>{getStatusBadge(request.statut)}</TableCell>
                    <TableCell>
                      <p className="text-sm">
                        {formatDate(request.date_declaration)}
                      </p>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {/* View Details */}
                          <DropdownMenuItem
                            onClick={() => handleViewDetails(request)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Voir détails
                          </DropdownMenuItem>

                          {/* Download justification if available */}
                          {request.justification && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() =>
                                  handleDownloadJustification(
                                    request.id,
                                    request.justification.fileName ||
                                      `justification_${request.id}`
                                  )
                                }
                                className="text-blue-600"
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Télécharger justificatif
                              </DropdownMenuItem>
                            </>
                          )}

                          {/* Delete option for employees (only for pending requests) */}
                          {userRole === "employe" &&
                            request.employe_id === currentUserId &&
                            request.statut === "declaree" && (
                              <>
                                <DropdownMenuSeparator />
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <DropdownMenuItem
                                      onSelect={(e) => e.preventDefault()}
                                      className="text-red-600"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Supprimer
                                    </DropdownMenuItem>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>
                                        Supprimer la demande
                                      </AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Êtes-vous sûr de vouloir supprimer cette
                                        demande d'absence ? Cette action est
                                        irréversible.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>
                                        Annuler
                                      </AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() =>
                                          handleDeleteRequest(request.id)
                                        }
                                        disabled={processingId === request.id}
                                        className="bg-red-600 hover:bg-red-700"
                                      >
                                        {processingId === request.id
                                          ? "Suppression..."
                                          : "Supprimer"}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}

                          {/* Approval actions for managers */}
                          {canManageRequests &&
                            request.statut === "declaree" && (
                              <>
                                <DropdownMenuSeparator />
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <DropdownMenuItem
                                      onSelect={(e) => e.preventDefault()}
                                      className="text-green-600"
                                    >
                                      <Check className="h-4 w-4 mr-2" />
                                      Approuver
                                    </DropdownMenuItem>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>
                                        Approuver la demande
                                      </AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Êtes-vous sûr de vouloir approuver cette
                                        demande d'absence de{" "}
                                        {request.employe?.prenom}{" "}
                                        {request.employe?.nom} ?
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>
                                        Annuler
                                      </AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() =>
                                          handleApproval(request.id, true)
                                        }
                                        disabled={processingId === request.id}
                                        className="bg-green-600 hover:bg-green-700"
                                      >
                                        {processingId === request.id
                                          ? "Traitement..."
                                          : "Approuver"}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>

                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <DropdownMenuItem
                                      onSelect={(e) => e.preventDefault()}
                                      className="text-red-600"
                                    >
                                      <X className="h-4 w-4 mr-2" />
                                      Refuser
                                    </DropdownMenuItem>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>
                                        Refuser la demande
                                      </AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Êtes-vous sûr de vouloir refuser cette
                                        demande d'absence de{" "}
                                        {request.employe?.prenom}{" "}
                                        {request.employe?.nom} ?
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>
                                        Annuler
                                      </AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() =>
                                          handleApproval(request.id, false)
                                        }
                                        disabled={processingId === request.id}
                                        className="bg-red-600 hover:bg-red-700"
                                      >
                                        {processingId === request.id
                                          ? "Traitement..."
                                          : "Refuser"}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Détails de la demande d'absence
            </DialogTitle>
            <DialogDescription>
              Informations détaillées sur la demande sélectionnée
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="flex-1 overflow-y-auto pr-2 space-y-6">
              {/* Employee Info */}
              {canManageRequests && (
                <div className="border-b pb-4">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Informations employé
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Nom complet</p>
                      <p className="font-medium">
                        {selectedRequest.employe?.prenom}{" "}
                        {selectedRequest.employe?.nom}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Département</p>
                      <p className="font-medium">
                        {selectedRequest.employe?.departement?.nom || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Request Details */}
              <div className="border-b pb-4">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Détails de la demande
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Type d'absence</p>
                    <Badge variant="outline" className="mt-1">
                      {selectedRequest.type_absence?.nom}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-gray-500">Statut</p>
                    <div className="mt-1">
                      {getStatusBadge(selectedRequest.statut)}
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-500">Date de début</p>
                    <p className="font-medium">
                      {formatDate(selectedRequest.date_debut)}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Date de fin</p>
                    <p className="font-medium">
                      {formatDate(selectedRequest.date_fin)}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Durée</p>
                    <p className="font-medium">
                      {calculateDuration(
                        selectedRequest.date_debut,
                        selectedRequest.date_fin
                      )}{" "}
                      jour
                      {calculateDuration(
                        selectedRequest.date_debut,
                        selectedRequest.date_fin
                      ) > 1
                        ? "s"
                        : ""}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Date de demande</p>
                    <p className="font-medium">
                      {formatDate(selectedRequest.date_declaration)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Reason */}
              {selectedRequest.motif && (
                <div className="border-b pb-4">
                  <h3 className="font-semibold mb-2">Motif</h3>
                  <p className="text-sm bg-gray-50 p-3 rounded-lg">
                    {selectedRequest.motif}
                  </p>
                </div>
              )}

              {/* Justification */}
              {selectedRequest.justification && (
                <div className="border-b pb-4">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Justificatif
                  </h3>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          {selectedRequest.justification.fileName}
                        </p>
                        <p className="text-sm text-gray-500">
                          Taille:{" "}
                          {(
                            selectedRequest.justification.fileSize / 1024
                          ).toFixed(1)}{" "}
                          KB
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          handleDownloadJustification(
                            selectedRequest.id,
                            selectedRequest.justification.fileName
                          )
                        }
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Télécharger
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Approval Info */}
              {selectedRequest.statut !== "declaree" && (
                <div>
                  <h3 className="font-semibold mb-2">
                    Informations d'approbation
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Statut</p>
                      <div className="mt-1">
                        {getStatusBadge(selectedRequest.statut)}
                      </div>
                    </div>
                    {selectedRequest.date_approbation && (
                      <div>
                        <p className="text-gray-500">Date d'approbation</p>
                        <p className="font-medium">
                          {formatDate(selectedRequest.date_approbation)}
                        </p>
                      </div>
                    )}
                    {selectedRequest.approbateur && (
                      <div>
                        <p className="text-gray-500">Approuvé par</p>
                        <p className="font-medium">
                          {selectedRequest.approbateur.prenom}{" "}
                          {selectedRequest.approbateur.nom}
                        </p>
                      </div>
                    )}
                    {selectedRequest.commentaire_approbation && (
                      <div className="col-span-2">
                        <p className="text-gray-500">Commentaire</p>
                        <p className="text-sm bg-gray-50 p-2 rounded mt-1">
                          {selectedRequest.commentaire_approbation}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action buttons for managers - Fixed at bottom */}
          {selectedRequest &&
            canManageRequests &&
            selectedRequest.statut === "declaree" && (
              <div className="flex gap-2 pt-4 border-t mt-4 flex-shrink-0">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button className="flex-1 bg-green-600 hover:bg-green-700">
                      <Check className="h-4 w-4 mr-2" />
                      Approuver
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Approuver la demande</AlertDialogTitle>
                      <AlertDialogDescription>
                        Êtes-vous sûr de vouloir approuver cette demande
                        d'absence ?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => {
                          handleApproval(selectedRequest.id, true);
                          setDetailsOpen(false);
                        }}
                        disabled={processingId === selectedRequest.id}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Approuver
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="flex-1">
                      <X className="h-4 w-4 mr-2" />
                      Refuser
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Refuser la demande</AlertDialogTitle>
                      <AlertDialogDescription>
                        Êtes-vous sûr de vouloir refuser cette demande d'absence
                        ?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => {
                          handleApproval(selectedRequest.id, false);
                          setDetailsOpen(false);
                        }}
                        disabled={processingId === selectedRequest.id}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Refuser
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
